/**
 * Sequencer — bridge-executed N-lane grid sequencer.
 *
 * Mental model:
 *   - One sequencer = one rectangular grid of lanes × steps. Lanes fire MIDI.
 *   - The sequencer has ONE length (in steps). All lanes share it — the
 *     grid is rectangular. This keeps the audible+visual model simple
 *     ("a sequencer is one loop"). Polyrhythm via independent per-lane
 *     lengths is reserved for a future advanced mode.
 *   - Each step is on/off + a velocity 1..127. Step duration = 16th notes
 *     by default (per-sequencer stepsPerBeat overrides).
 *   - Play flips the sequencer to launch-quantize on the next downbeat.
 *     Tap again to disarm/stop. Slaved to Live's transport when fresh,
 *     falls back to the bridge clock otherwise (same pattern as LFO/Macro).
 *
 *   Naming note: lanes mirror Macros' `MacroLane`. Same concept (parallel
 *   tracks inside a parent control) so the metaphor stays consistent.
 *
 * Schema-reserved (v1 ships basic UI; reserve in schema NOW so layouts
 * authored today are forward-compatible — see memory/feedback_basic_advanced_controls.md):
 *
 *   - Step.probability  — 0..1; future "skip" dice
 *   - Step.microOffset  — -0.5..0.5 step (swing / humanize)
 *   - Step.ratchets     — 1..8 sub-strikes within the step
 *   - Lane.direction    — forward / backward / pingpong / random
 *   - Lane.gate         — 0..1 fraction of a step the noteon holds
 *   - Lane.actions      — full ControlActions surface (universal trigger fanout)
 *   - Lane.lengthSteps  — RESERVED for future per-lane polyrhythm mode.
 *
 *   Runtime ignores fields it doesn't understand. The basic UI in v1 only
 *   exposes step on/off + velocity + per-lane note/cc/channel.
 *
 * Why bridge-side execution (vs PWA-side):
 *   The PWA may disconnect mid-set (browser refresh, WiFi hiccup, iPad
 *   sleep). A running drum pattern keeps gigging. The bridge owns the
 *   sequencer from arm to explicit kill.
 */

import type { LaneDmxTarget } from './fixture.js';

// =====================================================================
// MIDI target — same shape Macro uses
// =====================================================================

/** Which bridge instance runs this lane. v0: 'pc' only. */
export type SequencerBridgeTarget = 'pc' | 'mac' | 'all';

/** v1 supports note + cc only — same coverage as Macro. */
export type SequencerMidiKind = 'noteon' | 'cc';

export interface SequencerMidiTarget {
  bridge: SequencerBridgeTarget;
  kind: SequencerMidiKind;
  /** 0–15. */
  channel: number;
  /** For noteon: note number (e.g. 36 = C1 = kick). For cc: controller. 0..127. */
  number: number;
}

// =====================================================================
// Direction
// =====================================================================

/** Step traversal order. v1 only ships 'forward'; the other modes are
 *  reserved so layouts can store them and the future advanced UI
 *  exposes them without a schema migration. */
export type SequencerDirection = 'forward' | 'backward' | 'pingpong' | 'random';

// =====================================================================
// Step
// =====================================================================

export interface SequencerStep {
  /** When true, this step fires its lane's MIDI target on its tick. */
  on: boolean;
  /** Velocity 1..127. Default 100 if not set when on=true. */
  velocity?: number;
  /** RESERVED v1 — 0..1. Step fires only if Math.random() < probability.
   *  Undefined = always fires. */
  probability?: number;
  /** RESERVED v1 — fraction of a step (-0.5..0.5) to nudge the trigger.
   *  Positive = late (swing). Negative = early. */
  microOffset?: number;
  /** RESERVED v1 — 1..8. Sub-strikes within the step (a "roll"). */
  ratchets?: number;
}

// =====================================================================
// Lane
// =====================================================================

export interface SequencerLane {
  /** Stable id within the sequencer. ULID. */
  id: string;
  /** Human label (e.g. "Kick", "Snare"). Editor-only. */
  label?: string;
  /** Where this lane sends: MIDI, or a light (`LaneDmxTarget`, told apart
   *  by `isDmxLaneTarget`; a MIDI target has no `type`). */
  target: SequencerMidiTarget | LaneDmxTarget;
  /** The actual step grid. Length should equal the parent sequencer's
   *  lengthSteps; if shorter the bridge pads with off-steps; if longer,
   *  the bridge truncates. */
  steps: SequencerStep[];
  /** Gate length as a fraction of one step (0..1). Default 0.8 = note
   *  held 80% of the step duration. Past 1.0 = legato (note still on
   *  when next step starts; bridge sends noteoff just before the next
   *  noteon). RESERVED v1 — editor doesn't expose, runtime honors. */
  gate?: number;
  /** RESERVED v1 — traversal direction. Defaults to 'forward'. */
  direction?: SequencerDirection;
  /** Editor-only display color. Mirrors LayoutColor in projectStore.ts. */
  color?: string;
  /** RESERVED v1 — full Universal Trigger action surface for the
   *  Advanced modal. v1 runtime uses `target` exclusively. Stored as
   *  unknown to keep the schema package free of PWA types — the PWA
   *  side will narrow this when the advanced editor lands. */
  actions?: unknown;
  /** RESERVED v1 — per-lane length for future polyrhythm mode. v1
   *  ignores this and uses the parent Sequencer's lengthSteps. */
  lengthSteps?: number;
}

// =====================================================================
// Sequencer
// =====================================================================

export interface Sequencer {
  /** Stable id. Referenced from layout items as `sequencerId`. */
  id: string;
  /** ms epoch of the last local edit. Decides which copy wins when project
   *  sync brings the same sequencer from another device (pwa
   *  `layout/libraryMerge.ts`). Optional: added 2026-09-28, absent on older
   *  entries, which compare as 0. Additive, no schema bump. */
  modifiedAt?: number;
  /** Display label. Shown in the editor; user-renamable. */
  name: string;
  /** Lanes fire top-to-bottom in the editor. */
  lanes: SequencerLane[];
  /** Number of steps in the grid. ALL lanes share this length. Bounded
   *  1..64. */
  lengthSteps: number;
  /** Subdivisions per beat. Default 4 = 16th notes (16 steps = 4 beats =
   *  1 bar at 4/4). 2 = 8th notes. 8 = 32nd notes. */
  stepsPerBeat: number;
  /** RESERVED v1 — multi-pattern slots ("A", "B", "C", "D"). v1 only
   *  uses one pattern; the field exists so future pattern-switching
   *  doesn't migrate. */
  patterns?: Record<string, SequencerLane[]>;
  /** RESERVED v1 — active pattern key. Defaults to first key of
   *  `patterns`, or main lanes when patterns is undefined. */
  activePattern?: string;
  /** RESERVED v1 — incoming MIDI binding that arms this sequencer
   *  just like a tap. Reserved for Universal Trigger System work. */
  midiTrigger?: {
    kind: 'noteon' | 'cc';
    channel: number;
    number: number;
  };
}

// =====================================================================
// Library document
// =====================================================================

export const SEQUENCERS_SCHEMA_VERSION = 'opbuttons-sequencers/v3' as const;

export interface SequencerLibrary {
  schemaVersion: typeof SEQUENCERS_SCHEMA_VERSION;
  sequencers: Sequencer[];
}

// =====================================================================
// Helpers — exported so PWA editor and bridge can share invariants
// =====================================================================

/** Resolve a step's effective velocity. Falls back to 100 when undefined
 *  or out of range. Used by the engine when emitting noteon. */
export function effectiveVelocity(step: SequencerStep): number {
  const v = step.velocity ?? 100;
  if (!Number.isFinite(v)) return 100;
  return Math.max(1, Math.min(127, Math.round(v)));
}

/** Resolve a lane's gate. Defaults to 0.8 (80% of step) when undefined. */
export function effectiveGate(lane: SequencerLane): number {
  const g = lane.gate ?? 0.8;
  if (!Number.isFinite(g)) return 0.8;
  return Math.max(0.05, Math.min(2.0, g));
}

/** A blank step with on=false. Used when expanding a lane's steps array
 *  to match the sequencer's declared lengthSteps. */
export function blankStep(): SequencerStep {
  return { on: false };
}
